import axios from 'axios';
import {
  API_SEND_SMS,
  API_TOKEN,
  API_SEND_MAIL,
  API_GET_ACTIVE_ROOMS,
  API_SET_ROOM_PRIVACY,
  API_KNOCK_USER_ROOM,
  API_ACKNOWLEDGE_KNOCK,
  API_GET_KNOCK_STATUS,
  API_CLEAR_KNOCK,
  API_GET_ROOM_KNOCKS,
  API_GET_ROOM_CONFIG,
  API_GET_PHONENUMBER,
  API_GET_ROOM_DID,
  API_GET_CONTACTS,
  API_VOICE_TOKEN,
  API_GET_USER_DETAILS,
  API_ROOM,
  API_CREATE_MEETING,
  API_GET_ALL_MEETINGS,
  API_GET_MEETING,
  API_UPDATE_MEETING_STATUS,
  API_GET_EXTENSION,
  API_GET_PERSON_ID,
  API_GET_FIRST_NAME,
  API_FILE_GET,
  API_FILE_AUTH,
  API_FILE_CREATE,
  API_FILE_DELETE,
  API_FILE_UPLOAD,
  API_FILE_DOWNLOAD,
  API_GET_CHAT_MESSAGES,
  API_SEND_CHAT_MESSAGE,
  API_JOIN_ROOM_CHAT,
} from '../constants';
import { IActiveRoom, IRoom, RoomConfig, RoomType } from '../types';
import { ApiResponse } from '../types/ApiResponse';
import { FileDetails } from '../types/ApiFileDetails';
import { transformFileApiResponse } from './transformFileApiResponse';

const API_TIMEOUT = 20000;

interface IPhoneNumberApi {
  data: [
    {
      PhoneNumber: number;
    }
  ];
  status: number;
}

export const apiGetFirstName = (PersonID: number) => {
  return fetch(`${API_GET_FIRST_NAME}/${PersonID}`);
};

export const apiGetPersonId = (domain: string, username: string) => {
  return fetch(`${API_GET_PERSON_ID}/${domain}/${username}`);
};

// gets phone extension data like PBX number
export const apiGetPhoneExtension = (extension: string, domain: string) => {
  return fetch(`${API_GET_EXTENSION}/${extension}/${domain}`);
};

/**
 * Utility function to wrap fetch. This function should be used for api calls to avoid
 * calling res.json() repeatedly
 * @param input string
 * @param init RequestInit
 * @returns Promise<T>
 */
const apiFetch = async <T>(input: string | URL | globalThis.Request, init?: RequestInit): Promise<T> => {
  // Abort the request if it takes too long
  const controller = new AbortController();
  const timerId = setTimeout(() => controller.abort(), API_TIMEOUT);
  try {
    const response = await fetch(input, { ...init, signal: controller.signal });
    clearTimeout(timerId);

    if (!response.ok) {
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }
    const contentType = response.headers.get('Content-Type') || '';
    if (contentType.includes('application/json')) {
      return (await response.json()) as T;
    } else if (contentType.includes('text/')) {
      return (await response.text()) as T;
    } else if (contentType.includes('application/octet-stream') || contentType.includes('image/')) {
      return (await response.blob()) as T;
    } else {
      throw new Error(`Unsupported content type: ${contentType}`);
    }
  } catch (err) {
    clearTimeout(timerId);
    throw err;
  }
};

// sends text messages
export const apiSendSms = (
  toPhones: string[],
  fromupn: string,
  tonames: string[],
  phoneNumber: number
): Promise<any> => {
  return apiFetch(API_SEND_SMS, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      tophonenumbers: toPhones,
      token: API_TOKEN,
      fromupn,
      tonames,
      phoneNumber,
    }),
  });
};

// sends mail
export const apiSendMail = (
  toEmails: string[],
  fromupn: string,
  phoneNumber: number,
  usernames: string[]
): Promise<any> => {
  return apiFetch(API_SEND_MAIL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      toemails: toEmails,
      token: API_TOKEN,
      fromupn,
      phoneNumber,
      usernames,
    }),
  });
};

// grabs activerooms ids based off of username and domain
export const apiGetActiveMeetRooms = (username: string, domain: string): Promise<IActiveRoom[]> => {
  return apiFetch<IActiveRoom[]>(`${API_GET_ACTIVE_ROOMS}/${username}/${domain}`);
};

// grabs roomconfig data based off of roomname
export const apiGetRoomConfig = async (roomname: string): Promise<RoomConfig> => {
  return apiFetch<RoomConfig>(`${API_GET_ROOM_CONFIG}/${roomname}`);
};

// sends privacy id for each room created
export const apiUpdateRoomPrivacy = async (username: string, roomprivacy: number) => {
  return await apiFetch(API_SET_ROOM_PRIVACY, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      token: API_TOKEN,
      username,
      roomprivacy,
    }),
  });
};
// sends the initial knock
export const apiPostRoomKnock = (knocker: string, roomowner: string) => {
  return apiFetch(API_KNOCK_USER_ROOM, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      knocker,
      roomowner,
      token: API_TOKEN,
    }),
  });
};
// sends the response so it knows the knock is received
export const apiPostKnockAcknowledge = (knocker: string, upn: string, knockresponse: number) => {
  return apiFetch(API_ACKNOWLEDGE_KNOCK, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      knocker,
      upn,
      knockresponse,
      token: API_TOKEN,
    }),
  });
};
// retrieves current status of knock
export const apiGetKnockStatus = (roomowner: string) => {
  return apiFetch<string[]>(`${API_GET_KNOCK_STATUS}/${roomowner}`);
};
// gets rid of old knocks
export const apiPostKnockClear = (knocker: string, roomowner: string, knockresponse: string) => {
  return apiFetch(API_CLEAR_KNOCK, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      knocker,
      roomowner,
      knockresponse,
      token: API_TOKEN,
    }),
  });
};
// grabs each roomknock
export const apiGetRoomKnocks = (username: string): Promise<string[] | null> => {
  return apiFetch(`${API_GET_ROOM_KNOCKS}/${username}`);
};

// obtains worksoftphonenumber
export const apiGetPhoneNumber = (username: string, domain: string) => {
  return apiFetch<IPhoneNumberApi>(API_GET_PHONENUMBER, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      Username: username,
      DomainName: domain,
      action: 'avatarasoftphonenumbersearch',
    }),
  });
};
// grabs DID which helps identify the specific room
export const apiGetRoomDid = async (username: string, domain: string): Promise<number | null> => {
  const roomConfig = await apiFetch<RoomConfig[]>(`${API_GET_ROOM_DID}/${username}@${domain}.local`);
  return roomConfig?.[0]?.RoomDID || null;
};
// obtains requested contact
export const apiGetContacts = (password: string, username: string, domain: string, searchstring: string) => {
  return fetch(API_GET_CONTACTS, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      username: `${username}@${domain}.local`,
      password,
      searchstring,
      token: API_TOKEN,
    }),
  });
};
// grabs token for authorizing phonecalls in ccvoice
export const apiGetVoiceToken = async (username: string, domain: string, ownNumber?: number) => {
  const headers = new window.Headers();
  const params = new window.URLSearchParams({
    username,
    domain,
    did: String(ownNumber),
  });
  return apiFetch<string>(`${API_VOICE_TOKEN}?${params}`, { headers });
  // return window.location.href.toLowerCase().includes('rrastogi') ?
  //   'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzY29wZSI6InNjb3BlOmNsaWVudDppbmNvbWluZz9jbGllbnROYW1lPXJyYXN0b2dpYXZhdGFyYWNsb3VkIHNjb3BlOmNsaWVudDpvdXRnb2luZz9hcHBTaWQ9QVBhOTNiMDk3MTA4ZjQxOGQ5ZDM3NjU2YTJjZTM2OGIxOSIsImlzcyI6IkFDOTZhNmQ4MWFjZTgxYjM4ZDgwMWU5MjY1N2M5NjY0YWEiLCJleHAiOjE2MTU0ODE3OTAsImlhdCI6MTYxNTQ3ODE5MH0.D3rbvXm8Zba2h2v-3oYK0Nry5sW-Ds6D6oVLz5F6eaA'
  //   :
  //   'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzY29wZSI6InNjb3BlOmNsaWVudDppbmNvbWluZz9jbGllbnROYW1lPWdwaWxsYWF2YXRhcmFjbG91ZCBzY29wZTpjbGllbnQ6b3V0Z29pbmc_YXBwU2lkPUFQYTkzYjA5NzEwOGY0MThkOWQzNzY1NmEyY2UzNjhiMTkiLCJpc3MiOiJBQzk2YTZkODFhY2U4MWIzOGQ4MDFlOTI2NTdjOTY2NGFhIiwiZXhwIjoxNjA2MTI5MjE0LCJpYXQiOjE2MDYxMjU2MTR9.jX4o3i9Tec5bYu5M_YkUVJe2_VGTZJeGnfbGSEb86Rw';
};
// returns email among other details
export const apiGetUserDetails = (username: string, domain: string) => {
  return fetch(`${API_GET_USER_DETAILS}/${domain}/${username}`);
};
// sends created room data to database
export const apiPostRoomCreate = async (
  roomName: string,
  recordParticipants: boolean,
  type: RoomType = 'group',
  username: string
): Promise<{ message: string; room: IRoom; roomId: string; userId: string; rcAuthToken: string }> => {
  const response = await fetch(`${API_ROOM}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      type: type,
      name: roomName,
      recordParticipants,
      username,
    }),
  });
  return await response.json();
};

// sends status of disconnecting from room
export const apiPostRoomDisconnect = (roomSid: string) => {
  const url = `${API_ROOM}/${roomSid}`;
  const data = JSON.stringify({
    status: 'completed',
  });

  // Use sendBeacon for reliable delivery during page unload
  if (navigator.sendBeacon) {
    const blob = new Blob([data], { type: 'application/json' });
    return navigator.sendBeacon(url, blob);
  }

  // Fallback to fetch for older browsers
  return fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: data,
  });
};
// creates the meeting in ScheduledMeeting.tsx
export const apiPostMeetingCreate = (fromupn: string, meetingid: string, meetingstart: string, meetingend: string) => {
  return fetch(API_CREATE_MEETING, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      fromupn,
      meetingid,
      meetingstart,
      meetingend,
      token: API_TOKEN,
    }),
  });
};
// grabs all meetings created so far
export const apiGetMeetings = (fromupn: string) => {
  return fetch(`${API_GET_ALL_MEETINGS}/${fromupn}`);
};
// grabs individual meeting by id
export const apiGetMeetingById = (meetingId: string) => {
  return fetch(`${API_GET_MEETING}/${meetingId}`);
};
// sends updated status information
export const apiUpdateMeetingStatus = (meetingId: string, status: number) => {
  return fetch(API_UPDATE_MEETING_STATUS, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      meetingid: meetingId,
      status: status,
      token: API_TOKEN,
    }),
  });
};

// Get files
export const apiGetFiles = async (path: string) => {
  const params = new URLSearchParams({ path });
  const res = await apiFetch<ApiResponse<FileDetails>>(`${API_FILE_GET}?${params}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include', // Required for cookies
  });
  return transformFileApiResponse(res.data);
};

// File auth
export const apiFileAuth = async (userId: string, password: string) => {
  return apiFetch(API_FILE_AUTH, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({
      userId,
      password,
    }),
  });
};

// Create folder
export const apiCreateFolder = async (name: string, path: string) => {
  const params = new URLSearchParams({ path, name });
  const res = await apiFetch<ApiResponse<string>>(`${API_FILE_CREATE}?${params}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });
  return res;
};

// Delete folder/file
export const apiDeleteFolder = async (name: string, path: string) => {
  const params = new URLSearchParams({ path, name });
  const res = await apiFetch(`${API_FILE_DELETE}?${params}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });
  return res;
};

export const apiUploadFile = async (formData: FormData) => {
  const res = await apiFetch(`${API_FILE_UPLOAD}`, {
    method: 'POST',
    body: formData,
    credentials: 'include',
  });
  return res;
};

export const apiDownloadFile = async (name: string, path: string, isExcelFile?: boolean): Promise<Blob> => {
  const response = await axios.get(`${API_FILE_DOWNLOAD}`, {
    params: { filename: name, filepath: path },
    responseType: isExcelFile ? 'arraybuffer' : 'blob',
    withCredentials: true,
  });
  return response.data;
};

export const apiFetchChatMessages = async (roomName: string) => {
  try {
    const url = API_GET_CHAT_MESSAGES.replace('%roomname%', roomName);
    const res = await axios.get(url);
    return res.data.messages;
  } catch (err) {
    console.error('Failed to load chat messages', err);
  }
};

export const apiSendChatMessage = async ({
  message,
  roomName,
  username,
}: {
  roomName: string;
  username: string;
  message: string;
}) => {
  const result = await axios.post(API_SEND_CHAT_MESSAGE, {
    username,
    roomName,
    text: message,
  });
  return result.data.success;
};

export type ApiJoinRoomAndChatResponse = {
  success: true;
  rcRoomId: string;
  rcUserId: string;
  rcAuthToken: string;
  room: IRoom;
};

export const apiJoinRoomAndChat = async (username: string, roomName: string): Promise<ApiJoinRoomAndChatResponse> => {
  try {
    const result = await axios.post<Promise<ApiJoinRoomAndChatResponse>>(API_JOIN_ROOM_CHAT, {
      username,
      roomName,
    });
    return result.data;
  } catch (err) {
    console.error('Failed to join room and chat', err);
    throw err;
  }
};
